import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MEDIA } from "../media";
import { home } from "../content/home";
import { useContent, Rich, SlotVideo, useInView, prefersReducedMotion } from "../ui/helpers";

/*
  "One bundle at a station" — replaces the earlier factory clips (removed as
  confidential). An animated illustration: the needle runs, KS-101 reads each
  stitch and sends signals to the station terminal, which counts the bundle.
  If a stock clip is set in media.js ("station.video"), it is shown instead of
  the drawn machine. Auto-plays only while visible; stops for reduced motion;
  pause button and clickable steps.
  Text: content/home.js → station
*/
const STEP_TIME = [2600, 2600, 4200, 2800];
const PIECES = 24;

function Machine({ sewing, label }) {
  return (
    <svg className={`machine${sewing ? " sewing" : ""}`} viewBox="0 0 560 360" role="img" aria-label={label}>
      <defs>
        <linearGradient id="mBody" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E9F0F3" /><stop offset="1" stopColor="#BFCDD4" /></linearGradient>
        <linearGradient id="mFab" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#1F4E79" /><stop offset="1" stopColor="#2B6296" /></linearGradient>
      </defs>
      {/* table */}
      <rect x="10" y="262" width="540" height="14" rx="3" fill="#183B52" />
      <rect x="40" y="276" width="10" height="70" fill="#123044" /><rect x="500" y="276" width="10" height="70" fill="#123044" />
      {/* bed + pillar + arm + head */}
      <path d="M70 232 H400 a8 8 0 0 1 8 8 V262 H62 V240 a8 8 0 0 1 8 -8Z" fill="url(#mBody)" stroke="#0B2434" strokeOpacity=".35" />
      <path d="M318 232 V110 a26 26 0 0 0 -26 -26 H150 a22 22 0 0 0 -22 22 V158 H176 V128 H300 V232Z" fill="url(#mBody)" stroke="#0B2434" strokeOpacity=".35" />
      <rect x="128" y="150" width="48" height="30" rx="5" fill="#D3DDE2" stroke="#0B2434" strokeOpacity=".3" />
      {/* fabric on the bed with the stitch line running under the needle */}
      <path d="M30 226 L300 226 L318 236 L12 236 Z" fill="url(#mFab)" />
      <line className="seam" x1="20" y1="231" x2="152" y2="231" stroke="#E8EFF2" strokeWidth="2" strokeDasharray="6 5" />
      {/* needle bar */}
      <g className="needle">
        <rect x="146" y="176" width="10" height="30" fill="#9FB1BA" />
        <rect x="150" y="204" width="2.5" height="20" fill="#E8EFF2" />
      </g>
      <rect x="138" y="222" width="26" height="6" rx="2" fill="#7F949E" />
      {/* hand wheel */}
      <g className="wheel" style={{ transformOrigin: "336px 122px" }}>
        <circle cx="336" cy="122" r="26" fill="#27465A" stroke="#E8EFF2" strokeOpacity=".5" strokeWidth="2" />
        <path d="M336 98 V146 M312 122 H360" stroke="#E8EFF2" strokeOpacity=".55" strokeWidth="3" />
      </g>
      {/* thread spool */}
      <rect x="226" y="62" width="14" height="22" rx="2" fill="#16E6B2" /><line x1="233" y1="62" x2="233" y2="48" stroke="#9FB1BA" strokeWidth="2" />
      {/* KS-101 unit */}
      <g className="ks">
        <rect x="402" y="182" width="78" height="50" rx="7" fill="#0B2434" stroke="#16E6B2" strokeOpacity=".7" />
        <text x="441" y="212" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="13" fill="#E8EFF2">KS-101</text>
        <circle className="led" cx="470" cy="192" r="4" fill="#16E6B2" />
      </g>
      {/* cable to terminal + signal pulses */}
      <path id="cable" d="M480 207 C 520 207, 520 150, 560 150" fill="none" stroke="#1EC8FF" strokeOpacity=".45" strokeWidth="2" strokeDasharray="3 5" />
      {sewing && [0, 0.5, 1].map((d) => (
        <circle key={d} r="4.5" fill="#1EC8FF" className="pulse">
          <animateMotion dur="1.5s" begin={`${d}s`} repeatCount="indefinite" path="M480 207 C 520 207, 520 150, 560 150" />
        </circle>
      ))}
    </svg>
  );
}

export function StationDemo() {
  const c = useContent(home).station;
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.25 });
  const reduce = prefersReducedMotion();
  const [step, setStep] = useState(reduce ? 2 : 0);
  const [count, setCount] = useState(reduce ? 14 : 0);
  const [hold, setHold] = useState(reduce);
  const stock = MEDIA["station.video"]?.src;

  useEffect(() => {
    if (hold || !inView) return undefined;
    const t = setTimeout(() => setStep((s) => (s + 1) % 4), STEP_TIME[step]);
    return () => clearTimeout(t);
  }, [step, inView, hold]);

  useEffect(() => {
    if (step < 2) { setCount(0); return undefined; }
    if (step === 3) { setCount(PIECES); return undefined; }
    if (hold) { setCount((n) => n || 14); return undefined; }
    setCount(0);
    const t = setInterval(() => setCount((n) => (n >= PIECES ? n : n + 1)), 160);
    return () => clearInterval(t);
  }, [step, hold]);

  const t = c.terminal;
  const state = t.states[step];
  const val = (show, v) => <span className={show ? undefined : "blank"}>{show ? v : "—"}</span>;
  const sewing = step === 2 && !reduce && inView; // machine runs during "Start sewing"

  return (
    <section className="floor station" id="station" ref={ref} aria-labelledby="station-title">
      <div className="k-wrap">
        <div className="floor-head rv">
          <div>
            <p className="k-label">{c.eyebrow}</p>
            <h2 id="station-title" className="h-sec">{c.title}</h2>
          </div>
          <div className="floor-head-r">
            <p><Rich text={c.body} /></p>
            <div className="row">
              <Link className="k-btn k-btn-ghost k-btn-sm" to="/contact">{c.cta} <span className="ar" aria-hidden="true">→</span></Link>
              <span className="floor-note">{c.note}</span>
            </div>
          </div>
        </div>

        <div className="rig rv">
          <figure className="rig-machine">
            {stock ? <SlotVideo slot="station.video" toggle labels={{ pause: c.pause, play: c.play }} /> : <Machine sewing={sewing} label={c.machineLabel} />}
            <figcaption>
              <span>{c.machineLabel}</span>
              <span className="rig-count"><b>{count * 38}</b> {t.stitches}</span>
            </figcaption>
          </figure>

          <div className="rig-term">
            <div className="term" role="img" aria-label={`${t.aria}: ${state.msg.replace(/\[\[|\]\]/g, "")}`}>
              <div className="term-top"><b>{t.station}</b><span className="on">{t.online}</span></div>
              <div className="term-meta">
                <div><small>{t.operator}</small>{val(true, t.operatorValue)}</div>
                <div><small>{t.operation}</small>{val(true, t.operationValue)}</div>
                <div><small>{t.style}</small>{val(step >= 1, t.styleValue)}</div>
                <div><small>{t.size}</small>{val(step >= 1, t.sizeValue)}</div>
              </div>
              <div className="term-stage">
                <div className="ring" style={{ "--p": (count / PIECES) * 100 }}>
                  <div style={{ textAlign: "center" }}><b>{count}</b><i>{t.of}</i></div>
                </div>
                <p className="term-msg"><Rich text={state.msg} /></p>
              </div>
              <div className={`term-btn${step === 1 || step === 3 ? " go" : ""}`}>{state.btn}</div>
              <div className="term-foot"><span>{t.bundle}</span><span>K SMART V2</span></div>
            </div>
          </div>
        </div>

        <ol className="stepper" aria-label={c.stepsLabel} style={{ "--fill": `${(step / 3) * 100}%` }}>
          {c.steps.map((s, i) => (
            <li key={s.title} className={i < step ? "done" : i === step ? "on" : undefined}>
              <button type="button" aria-current={i === step ? "step" : undefined} onClick={() => { setStep(i); setHold(true); }}>
                <strong>{s.title}</strong><span>{s.body}</span>
              </button>
            </li>
          ))}
        </ol>
        <p className="station-ctrl">
          <button type="button" className="tbtn" onClick={() => setHold((h) => !h)} aria-pressed={hold}>
            {hold ? `▶ ${c.play}` : `❚❚ ${c.pause}`}
          </button>
        </p>
      </div>
    </section>
  );
}
